import * as React from "react";
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux";
import { fetchReOpenReasonList } from '../../containers/WorkkardDetail/actions/action';
import { useModules } from '../../utils/modules';
import ChooseActivityTemplate from "../ChooseActivityTemplate";
import { ReopenConstants } from '../../containers/WorkkardDetail/constants';
import * as CommonUtil from '../../common/util';
import Icon from '../../icons';
import { showDialogueBox, hideDialogueBox } from '../DialogueBox/actions';
import AddEditActivity from '../../containers/CreationForms/AddEditActivity';
import ActOptionForReopenWk from '../ReopenWorkkard/ActOptionForReopenWk';
import ReopenReasons from "./wrkdReopenReason";
import { selectSessionContextOidc } from '@container/AdminPanel/features/users/usersSelectors';
import { PATHS } from "../../router/paths"

const DEFAULT_REOPEN_REASON_TEXT = "Admin";
const DEFAULT_REOPEN_SUB_REASON_TEXT = "";

const ChoseReason = (props) => {
    const { isModuleEnabled } = useModules();
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const wrkdReOpenReasonData = useSelector(
        (state) => state.workkardDetail?.reOpenReasonList?.wrkdReOpenReasonData ?? {},
    );
    const workkard = useSelector((state) => state.workkardDetail.workkardDetail);
    const sessionContext = useSelector(selectSessionContextOidc);
    const userView = sessionContext?.userView;

    const [reason, setReason] = React.useState(null);
    const [activity, setActivityState] = React.useState(null);
    const [isSetReOpenActivityForClosedWk, setIsSetReOpenActivityForClosedWk] = React.useState(false);
    const [subReasonsList, setSubReasonsList] = React.useState(null);
    const [reopenSubReason, setReopenSubReason] = React.useState(null);
    const [showSubReason, setShowSubReason] = React.useState(false);

    const reasonEnabled = isModuleEnabled("workkards.reopen.reason_enabled");

    React.useEffect(() => {
        if (workkard?.TemplateId && reasonEnabled) {
            dispatch(fetchReOpenReasonList({ templateId: workkard.TemplateId }));
        }
    }, [dispatch, workkard?.TemplateId, reasonEnabled]);

    const closeDialogBox = () => {
        dispatch(hideDialogueBox());
    };

    const openCreateActivityModal = (activityData, reasonId = 0, subReasonId = 0, isClone = false) => {
        if(props.onClose) props.onClose()
        else dispatch(hideDialogueBox())

        const templateShortName = activityData?.TemplateShortName ?? activityData?.template_short_name;
        const templateQuery = templateShortName ? `&template=${templateShortName}` : '';

        navigate(
            `${PATHS.ACTIVITY_CREATE.replace(':workkardId', props.wkid)}?wrrId=${encodeURIComponent(reasonId)}&wrsId=${encodeURIComponent(subReasonId)}${templateQuery}`,
            {
                state: {
                    tmplId: activityData?.Id ?? 0,
                    wrrId: reasonId,
                    wrkdReopenSubreasonId: subReasonId,
                    workkardId: props.wkid,
                    isClone,
                    referrer: props.referrer || '',
                    ...(isClone && props.actId ? {actId: props.actId} : {}),
                    returnUrl: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                }
            }
        )
    };

    const isWorkkardClosedForOrg = () =>
        workkard?.WorkkardStatus?.toUpperCase() === 'CLOSE'
        && workkard?.OrganizationId === userView?.orgId;

    const setActivity = (act) => {
        const showReason = isWorkkardClosedForOrg();
        if (!showReason) {
            openCreateActivityModal(act);
            return;
        }
        setActivityState(act);
    };

    const setReOpenActivityForClosedWk = (activityData) => {
        setActivityState(activityData);
        setIsSetReOpenActivityForClosedWk(true);
    };

    const confirm = () => {
        const { isCreateActivity, isCloneActivity, isReopenActivity } = props;
        const showReason = isWorkkardClosedForOrg();
        const reasonEnabled = isModuleEnabled("workkards.reopen.reason_enabled");
        const reasonRequired = showReason && reasonEnabled;

        if (
            (isCreateActivity && !activity)
            || (reasonRequired && !reason)
            || (reasonEnabled && subReasonsList && subReasonsList.length > 0 && !reopenSubReason)
        ) {
            return;
        }

        const reasonIdValue = reasonEnabled ? (reason?.Id ?? 0) : 0;
        const reasonTextValue = reasonEnabled ? reason?.Reason : DEFAULT_REOPEN_REASON_TEXT;
        const subReasonIdValue = reasonEnabled ? (reopenSubReason?.Id ?? 0) : 0;
        const subReasonTextValue = reasonEnabled
            ? (reopenSubReason != null ? reopenSubReason.Reason : "")
            : DEFAULT_REOPEN_SUB_REASON_TEXT;

        if (isSetReOpenActivityForClosedWk) {
            const data = {
                ActivityId: activity.activityId,
                cId: activity.workkardId,
                actionType: "ReOpen",
                wrrId: reasonIdValue,
                wrr: reasonTextValue,
                wrSubReason: subReasonTextValue,
            };
            props.onConfirmClick(data);
            return;
        }

        if (isReopenActivity) {
            props.onConfirmClick({
                reasonId: reasonIdValue,
                reason: reasonTextValue,
                subReason: subReasonTextValue,
                subReasonId: subReasonIdValue,
            });
            return;
        }

        if (isEditActivity) {
            props.onConfirmClick({
                reasonId: reason?.Id ?? 0,
                subReasonId: reopenSubReason?.Id ?? 0,
            });
            return;
        }

        if (isCloneActivity) {
            openCreateActivityModal(activity, reason?.Id ?? 0, reopenSubReason?.Id ?? 0, true);
            return;
        }

        openCreateActivityModal(
            activity,
            reasonEnabled ? reasonIdValue : reasonTextValue,
            reasonEnabled ? subReasonIdValue : subReasonTextValue,
            false,
        );
    };

    const sortedReOpenReasonList = () => {
        if (!wrkdReOpenReasonData || Object.keys(wrkdReOpenReasonData).length === 0) return [];
        const list = wrkdReOpenReasonData.reasons ?? [];
        const sortedList = CommonUtil.sortArrayByKey(list, 'Reason');
        const index = sortedList.findIndex((obj) => obj.Reason === 'Admin');
        if (index !== -1) {
            const adminObj = sortedList.splice(index, 1)[0];
            sortedList.push(adminObj);
        }
        return sortedList;
    };

    const resetActivity = () => {
        setActivityState(null);
        setIsSetReOpenActivityForClosedWk(false);
    };

    const reasonSelected = (item) => {
        setReason(item);
        const subs = wrkdReOpenReasonData?.subReasons ?? [];
        const matched = subs.filter((x) => x.ParentId === item.Id);
        if (matched.length > 0) {
            setShowSubReason(true);
            setReopenSubReason(null);
            setSubReasonsList(matched.sort((a, b) => a.Id - b.Id));
        } else {
            setShowSubReason(false);
            setSubReasonsList([]);
            setReopenSubReason(null);
        }
    };

    const subReasonSelected = (item) => {
        setReopenSubReason(item);
    };

    const {
        isCreateActivity,
        isCloneActivity,
        isReopenActivity,
        isEditActivity,
        isReopenWorkkard = false,
        showNoteText = true,
    } = props;
    const showReason = isWorkkardClosedForOrg();
    const reasonRequired = showReason && reasonEnabled;

    let disabled = false;
    if (
        (isCreateActivity && !activity)
        || (reasonRequired && !reason)
        || (reasonEnabled && subReasonsList && subReasonsList.length > 0 && !reopenSubReason)
        || (!activity && isReopenWorkkard)
    ) {
        disabled = true;
    }

    const reOpenReasonList = sortedReOpenReasonList();

    let noteText = '';
    let subText = '';
    if (isCreateActivity) {
        noteText = ReopenConstants.createActivityNote;
        subText = reasonEnabled ? ReopenConstants.createActivitySubtext : '';
    } else if (isCloneActivity) {
        noteText = ReopenConstants.cloneActivityNote;
        subText = ReopenConstants.createActivitySubtext;
    } else if (isReopenActivity) {
        noteText = reasonEnabled ? ReopenConstants.reOpenActivityNote : ReopenConstants.reOpenActivityNoteHideReason;
        subText = reasonEnabled ? ReopenConstants.reOpenSubtext : '';
    } else if (isReopenWorkkard) {
        subText = reasonEnabled ? ReopenConstants.reopenWorkkardSubtext : '';
    } else if (isEditActivity) {
        noteText = ReopenConstants.editActivityNote;
        subText = ReopenConstants.editActivitySubtext;
    }

    return (
        <React.Fragment>
            <div className="modalHeader rowSpaceBetween alignMiddle">
                <h1>{props.title}</h1>
                {props.isCreateActivity && !showReason ? (
                    <>
                        <div className="alignMiddle">
                            <span className="badges orange mr">W</span>
                            <span className="status wkNumber">{workkard?.WorkkardNumber}</span>
                        </div>
                        <a
                            className="close"
                            href="javascript:void(0)"
                            onClick={
                                props.isAddActivityEvent
                                    ? () => props.onClose({ wkId: props.wkid })
                                    : props.onClose
                            }
                        >
                            <Icon name="close" className="icon ml__1" />
                        </a>
                    </>
                ) : null}
            </div>

            {showReason ? (
                <div className="modalBody container close_activity_popup">
                    {workkard?.litClaimStatusForWorkkard
                        && workkard.litClaimStatusForWorkkard !== ""
                        && workkard.litClaimStatusForWorkkard !== "Closed" ? (
                        <div className="errorToast mb alignMiddle">
                            <span className="selfStart assets">
                                <Icon name="warningMessage" className="icon mr" />
                            </span>
                            <span>
                                There is an open Litigation Workkard in Griston Litigation Group organization associated with this Workkard.
                            </span>
                        </div>
                    ) : null}
                    {!reasonEnabled ? (
                        showNoteText && noteText ? <p>{noteText}</p> : null
                    ) : workkard?.litClaimStatusForWorkkard
                        && (workkard.litClaimStatusForWorkkard !== "" || workkard.litClaimStatusForWorkkard !== "Closed")
                        && showNoteText ? (
                        <div className="infoToast mb alignMiddle">
                            <span className="selfStart assets">
                                <Icon name="infoActivity" className="icon mr" />
                            </span>
                            <span>{noteText}</span>
                        </div>
                    ) : (
                        showNoteText && (
                            <p className="alert info">
                                <strong>Note: </strong>
                                {noteText}
                            </p>
                        )
                    )}

                    {subText ? (
                        <p className="rowFlex alignMiddle">
                            <label>{subText}</label>
                        </p>
                    ) : null}

                    <div className="activityTemplate col-2">
                        <ul className="wrapFlex">
                            {reOpenReasonList && reOpenReasonList.length > 0 && reasonEnabled ? (
                                <li>
                                    <label>Reopen Reason</label>
                                    <ReopenReasons
                                        wrkdReasonList={reOpenReasonList}
                                        defaultSelectedValue="Select Reopen Reason"
                                        selectedValue={reasonSelected}
                                        className="dropDown select"
                                    />
                                </li>
                            ) : null}

                            {showSubReason && subReasonsList && subReasonsList.length > 0 ? (
                                <li>
                                    <label>Reopen Sub-Reason</label>
                                    <ReopenReasons
                                        wrkdReasonList={subReasonsList}
                                        defaultSelectedValue="Select Sub-Reason"
                                        selectedValue={subReasonSelected}
                                        className="dropDown select"
                                    />
                                </li>
                            ) : null}
                        </ul>
                    </div>

                    {!isReopenWorkkard ? (
                        props.isCreateActivity && (
                            <ChooseActivityTemplate
                                showReason={showReason}
                                setActivity={setActivity}
                                {...props}
                            />
                        )
                    ) : (
                        <ActOptionForReopenWk
                            showReason={showReason}
                            setActivity={setActivity}
                            {...props}
                            resetActivity={resetActivity}
                            setReOpenActivity={setReOpenActivityForClosedWk}
                        />
                    )}
                </div>
            ) : null}

            {props.isCreateActivity && !showReason ? (
                <ChooseActivityTemplate
                    showReason={showReason}
                    setActivity={setActivity}
                    {...props}
                />
            ) : null}

            {showReason ? (
                <div className="modalFooter">
                    <div className="rowFlex alignRight">
                        <button onClick={props.onClose} className="btn link">Cancel</button>
                        <button
                            className={disabled ? "btn buttonGradient disabled" : "btn buttonGradient"}
                            disabled={disabled}
                            onClick={confirm}
                        >
                            <span>{props.isCreateActivity && showReason ? 'Submit' : 'Confirm'}</span>
                        </button>
                    </div>
                </div>
            ) : null}
        </React.Fragment>
    );
};

export default ChoseReason;